/**
 * Workspace Store
 * Holds saved custom workspaces (panel-layout presets) and the currently
 * active workspace id. Custom workspaces persist under the panel storage
 * namespace so panel layouts survive application reloads.
 *
 * Named preset definitions (the app's factory workspaces) stay app-side; this
 * store only owns the user's saved layouts.
 */

import { create } from 'zustand';
import { panelStorageKey } from './namespace';

export const DEFAULT_WORKSPACE_ID = 'default';

/** A workspace preset's panel-group spec, as declared by the app. */
export interface WorkspaceGroupSpec {
    /** Suffix appended to the panel id, e.g. "history-properties". */
    id: string;
    tabs: WorkspaceTabSpec[];
}

export interface WorkspaceTabSpec {
    id: string;
    label: string;
}

export interface CustomWorkspace {
    id: string;
    name: string;
    /** When false, applying the workspace leaves the current panel layout untouched. */
    capturePanelLocations: boolean;
    groups: WorkspaceGroupSpec[];
}

interface WorkspaceState {
    customWorkspaces: CustomWorkspace[];
    activeWorkspaceId: string;
    saveCustomWorkspace: (
        name: string,
        groups: WorkspaceGroupSpec[],
        capturePanelLocations?: boolean,
    ) => CustomWorkspace;
    deleteCustomWorkspace: (id: string) => void;
    getCustomWorkspace: (id: string) => CustomWorkspace | undefined;
    setActiveWorkspace: (id: string) => void;
}

const customWorkspacesKey = () => panelStorageKey('workspace.custom');
const activeWorkspaceKey = () => panelStorageKey('workspace.active');

const loadCustomWorkspaces = (): CustomWorkspace[] => {
    if (typeof window === 'undefined') return [];
    try {
        const raw = localStorage.getItem(customWorkspacesKey());
        const parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

// Workspaces captured before the store existed were written under this
// per-name prefix; they were unreachable by the menu, so remove them once.
const LEGACY_WORKSPACE_PREFIX = 'sidePanel.customWorkspace.';
const pruneLegacyWorkspaces = () => {
    if (typeof window === 'undefined') return;
    const stale: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(LEGACY_WORKSPACE_PREFIX)) stale.push(key);
    }
    stale.forEach((key) => localStorage.removeItem(key));
};
if (typeof window !== 'undefined') pruneLegacyWorkspaces();

const persistCustomWorkspaces = (workspaces: CustomWorkspace[]) => {
    if (typeof window === 'undefined') return;
    try {
        localStorage.setItem(
            customWorkspacesKey(),
            JSON.stringify(workspaces),
        );
    } catch {
        // Storage may be unavailable (quota/private mode); keep in-memory state.
    }
};

const loadActiveWorkspace = (): string => {
    if (typeof window === 'undefined') return DEFAULT_WORKSPACE_ID;
    try {
        return (
            localStorage.getItem(activeWorkspaceKey()) || DEFAULT_WORKSPACE_ID
        );
    } catch {
        return DEFAULT_WORKSPACE_ID;
    }
};

export const useWorkspaceStore = create<WorkspaceState>((set, get) => ({
    customWorkspaces: loadCustomWorkspaces(),
    activeWorkspaceId: loadActiveWorkspace(),

    saveCustomWorkspace: (name, groups, capturePanelLocations = true) => {
        const trimmedName = name.trim();
        // Reusing an existing name replaces that workspace's layout, keeping its id.
        const existing = get().customWorkspaces.find(
            (ws) => ws.name.toLowerCase() === trimmedName.toLowerCase(),
        );

        let next: CustomWorkspace[];
        let saved: CustomWorkspace;
        if (existing) {
            saved = {
                ...existing,
                name: trimmedName,
                groups,
                capturePanelLocations,
            };
            next = get().customWorkspaces.map((ws) =>
                ws.id === existing.id ? saved : ws,
            );
        } else {
            saved = {
                id: `workspace-${Date.now()}`,
                name: trimmedName,
                groups,
                capturePanelLocations,
            };
            next = [...get().customWorkspaces, saved];
        }

        persistCustomWorkspaces(next);
        set({ customWorkspaces: next });
        return saved;
    },

    deleteCustomWorkspace: (id) => {
        const next = get().customWorkspaces.filter((ws) => ws.id !== id);
        persistCustomWorkspaces(next);
        set({ customWorkspaces: next });
    },

    getCustomWorkspace: (id) =>
        get().customWorkspaces.find((ws) => ws.id === id),

    setActiveWorkspace: (id) => {
        if (typeof window !== 'undefined') {
            try {
                localStorage.setItem(activeWorkspaceKey(), id);
            } catch {
                // Storage unavailable: state stays in-memory only
            }
        }
        set({ activeWorkspaceId: id });
    },
}));
